import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';

import { dashboardUserListInvites } from '@column/api';

import { queryClient } from '~/lib/api/queryClient';
import { sandboxOptions } from '~/lib/api/sandboxOptions';
import { Action, checkAccess } from '~/lib/authz';
import { setMonitoringContext, startMonitoringSession, stopMonitoringSession } from '~/lib/monitoring';
import { LoginAuthMethod } from '~/lib/session/LoginAuthMethod';
import { PlatformModel, PlatformType } from '~/models/PlatformModel';
import { UserModel } from '~/models/UserModel';
import {
  DepgraphNodeValues,
  PlatformConfig,
  PlatformMeta,
  PlatformRepository,
} from '~/repositories/PlatformRepository';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { MFAStatus, PlatformRole, PlatformRolePermissions, Settings } from '~/repositories/UserRepository';
import { clearAppsContextCookies, deepMerge, deleteCookie, log, setAppsContextCookies, setCookie } from '~/util';

import { useConfigStore } from './Config';

const DEFAULT_SETTINGS: Partial<Settings> = {
  themeType: 'default',
  uploadSettings: {
    rdcFrontImageCrop: true,
    rdcBackImageCrop: true,
  },
  gettingStartedDismissed: false,
};

class PlatformError extends Error {
  constructor(message?: string | undefined, context?: any, consoleLog?: boolean) {
    super(message ?? 'Platform error');

    if (consoleLog !== false) {
      console.error('PlatformError:', message, { context }, this);
    }

    this.name = 'PlatformError';
  }
}

type SessionStoreListener = () => void;
type SessionStoreEvent = 'signin' | 'signout' | 'change';

interface CurrentState {
  currentUser?: UserModel;
  currentPlatform?: PlatformModel;
  currentPermission?: PlatformRolePermissions;
  currentPermissionName?: string;
  platformRole?: PlatformRole;
}

interface LoadSessionOptions {
  preferredPlatformId?: string;
  showGlobalLoading?: boolean;
}

const selectPlatformRole = (
  platformRoles: PlatformRole[],
  userId: string,
  defaultPlatformId: string,
  preferredPlatformId?: string
) => {
  const userPlatformRoles = platformRoles.filter((role) => role.dashboardUserId === userId);

  return (
    userPlatformRoles.find((role) => role.platformId === preferredPlatformId) ??
    userPlatformRoles.find((role) => role.platformId === defaultPlatformId) ??
    userPlatformRoles[0]
  );
};

const listDashboardUserInvites = async (dashboardUserId: string) => {
  const { data } = await dashboardUserListInvites({
    path: { dashboard_user_id: dashboardUserId },
    ...sandboxOptions,
    throwOnError: true,
  });
  return { invites: data.invites ?? [] };
};

interface SessionStoreState extends CurrentState {
  userId?: string;
  loggedIn?: boolean;
  params?: Record<string, string | undefined>;
  isLoading: boolean;
  isSandbox?: boolean;
  eventListeners: Map<SessionStoreEvent, SessionStoreListener[]>;
  loginAuthMethod?: LoginAuthMethod;
  mfaStatus: MFAStatus;
  settings: Partial<Settings>;
}

interface SessionStoreComputed {
  authRequired: () => boolean;
  isSignedIn: () => boolean;
}

interface SessionStoreAction {
  clearCookies: () => void;
  isAuthorized: (action: Action) => boolean;
  setLoading: (isLoading: boolean) => void;
  setSandbox: (isSandbox: boolean) => void;
  setSession: (
    sessionId: string,
    userId: string,
    options?: { loginAuthMethod?: LoginAuthMethod; mfaStatus?: MFAStatus }
  ) => void;
  setParams: (params?: Record<string, string | undefined>) => void;
  addParam: (key: string, value?: string) => void;
  setPlatform: (platform?: PlatformType & PlatformConfig & PlatformMeta, platformRole?: PlatformRole) => void;
  setSettings: (settings: Partial<Settings>) => void;
  updateSettings: (settings: Partial<Settings>, replaceField?: keyof Settings) => Promise<Partial<Settings>>;
  changeTheme: () => Promise<Partial<Settings> | undefined>;
  loadPlatform: ({ fetchMeta, user }: { fetchMeta?: boolean; user?: UserModel }) => Promise<PlatformModel | undefined>;
  refetchCurrentUser: () => Promise<void>;
  refetchPlatformConfig: () => Promise<void>;
  refetchPlatformSettings: () => Promise<void>;
  loadSession: (options?: LoadSessionOptions) => Promise<CurrentState>;
  signOut: () => void;
  triggerEvent: (event: SessionStoreEvent) => void;
  addEventListener: (event: SessionStoreEvent, listener: SessionStoreListener) => void;
  setCurrentUser: (user: UserModel) => void;
  setMfaStatus: (status?: MFAStatus) => void;
}

export const useSessionStore = create<SessionStoreState & SessionStoreComputed & SessionStoreAction>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        isLoading: true,
        isSandbox: true,
        eventListeners: new Map<SessionStoreEvent, SessionStoreListener[]>(),
        settings: DEFAULT_SETTINGS,
        mfaStatus: MFAStatus.NONE,

        isAuthorized: (action: Action) => {
          const platformRole = get().platformRole;
          if (!platformRole) {
            return false;
          }
          return checkAccess(platformRole, action);
        },

        isSignedIn: () => {
          return get().loggedIn === true && get().userId !== undefined;
        },

        authRequired: () => {
          return get().loggedIn === undefined;
        },

        clearCookies: () => {
          queryClient.clear();

          set(() => ({
            userId: undefined,
            loggedIn: undefined,
            loginAuthMethod: undefined,
          }));

          deleteCookie('session_id');
          clearAppsContextCookies();
        },

        setLoading: (isLoading: boolean) => {
          set(() => ({
            isLoading,
          }));
        },

        setSandbox: (isSandbox: boolean) => {
          const modeChanged = get().isSandbox !== isSandbox;
          set(() => ({
            isSandbox,
          }));

          const platformId = get().currentPlatform?.id ?? get().currentUser?.defaultPlatformId;
          if (platformId) {
            setAppsContextCookies({ platformId, isSandbox });
          }

          get().refetchPlatformSettings();
          if (modeChanged) {
            void get().refetchPlatformConfig();
          }
        },

        setSession: (
          sessionId: string,
          userId: string,
          options?: { loginAuthMethod?: LoginAuthMethod; mfaStatus?: MFAStatus }
        ) => {
          set(() => ({
            userId,
            loginAuthMethod: options?.loginAuthMethod,
            ...(options?.mfaStatus !== undefined ? { mfaStatus: options.mfaStatus } : {}),
          }));
          setCookie({
            key: 'session_id',
            value: sessionId,
          });
        },

        setMfaStatus: (status?: MFAStatus) => {
          set(() => ({
            mfaStatus: status,
          }));
        },

        setParams: (params?: Record<string, string | undefined>) => {
          set(() => ({
            params,
          }));
        },

        addParam: (key: string, value?: string) => {
          set((state) => ({
            params: {
              ...(state.params ?? {}),
              [key]: value,
            },
          }));
        },

        setPlatform: (
          platform?: (PlatformType & PlatformConfig & PlatformMeta) | PlatformType,
          platformRole?: PlatformRole
        ) => {
          const userId = get().currentUser?.id;
          const currentPlatformId = get().currentPlatform?.id;

          if (!userId) {
            throw new PlatformError("Can't set platform", { userId, platform, platformRole });
          }

          if (!platform && platformRole) {
            if (currentPlatformId && currentPlatformId !== platformRole.platformId) {
              queryClient.clear();
            }

            set(() => ({
              platformRole,
              currentPermission: platformRole?.platformLevelPermissions,
              currentPermissionName: platformRole?.name ? getRoleLabel(platformRole.name) : '',
            }));
            return true;
          }

          if (!platform) {
            return false;
          }

          if (userId && get().currentPlatform === platform) {
            return true;
          }

          if (currentPlatformId && currentPlatformId !== platform.id) {
            queryClient.clear();
          }

          const isSandbox = platform.isLiveEnabled ? get().isSandbox : true;
          const platformModel = new PlatformModel(platform);
          set(() => ({
            currentPermission: platformRole?.platformLevelPermissions,
            currentPlatform: platformModel,
            currentPermissionName: platformRole?.name ? getRoleLabel(platformRole.name) : '',
            isSandbox,
            platformRole,
          }));

          setAppsContextCookies({ platformId: platformModel.id, isSandbox });
          setMonitoringContext({ platform: platformModel });
          get().refetchPlatformSettings();

          return true;
        },

        setSettings: (settings: Partial<Settings>) => {
          set(() => ({
            settings,
          }));
        },

        updateSettings: async (settings: Partial<Settings>, replaceField?: keyof Settings) => {
          const { UserRepository } = await import('~/repositories');

          const partialUpdate = replaceField ? { [replaceField]: settings[replaceField] } : settings;

          const updatedSettings = deepMerge(get().settings, partialUpdate);

          get().setSettings(updatedSettings);

          return UserRepository.updateSettings(updatedSettings);
        },

        changeTheme: async () => {
          const next = get().settings.themeType === 'default' ? 'dark' : 'default';
          return get().updateSettings({ themeType: next });
        },

        loadPlatform: async (args?: { fetchMeta?: boolean; user?: UserModel }) => {
          const { fetchMeta, user } = args ?? {};
          const currentUserState = user ?? get().currentUser;
          const currentPlatformRole = get().platformRole;

          if (typeof currentUserState?.defaultPlatformId === 'undefined' || !currentUserState?.defaultPlatformId) {
            throw new PlatformError('Default platform id not found', { currentUserState, currentPlatformRole });
          }

          let platformType: PlatformType;
          try {
            platformType = await PlatformRepository.get(currentUserState.defaultPlatformId);
          } catch (err) {
            if (err === 'Unauthorized') {
              return undefined;
            }

            throw err;
          }

          if (!platformType.isLiveEnabled && !get().isSandbox) {
            get().setSandbox(true);
          }

          let platformConfig: PlatformConfig;
          try {
            platformConfig = await PlatformRepository.getConfig(currentUserState.defaultPlatformId);
          } catch (err) {
            if (err === 'Unauthorized') {
              return undefined;
            }

            throw err;
          }

          let platformDependencyGraph: DepgraphNodeValues = {};
          let platformMeta: PlatformMeta = {};
          if (fetchMeta) {
            try {
              [platformDependencyGraph, platformMeta] = await Promise.all([
                PlatformRepository.getDependencyGraph(currentUserState.defaultPlatformId),
                PlatformRepository.getMeta(currentUserState.defaultPlatformId),
              ]);
            } catch (err) {
              log({
                name: 'failed to load platform metadata',
                context: { error: err },
                type: 'warn',
              });
            }
          }

          const platform = {
            ...platformType!,
            ...platformMeta,
            checkIssuingCustomCheckNumbersEnabled: platformConfig.checkIssuingCustomCheckNumbersEnabled,
            checkIssuingMailingEnabled: platformConfig.checkIssuingMailingEnabled,
            issuerProcessingDashboardEnabled: platformConfig.issuerProcessingDashboardEnabled,
            correspondentBankingEnabled: platformConfig.correspondentBankingEnabled,
            depgraphNodeValues: platformDependencyGraph,
            transferApprovalConfig: platformConfig.transferApprovalConfig,
          };

          get().setPlatform(platform, currentPlatformRole);
          return new PlatformModel(platform);
        },

        refetchCurrentUser: async () => {
          try {
            const { UserRepository } = await import('~/repositories');
            const user = await UserRepository.fetchSelf();

            const [{ platformRoles }, { invites: platformInvites }] = await Promise.all([
              UserRepository.platformRoles({ dashboardUserId: user.id }),
              listDashboardUserInvites(user.id),
            ]);

            const platformRole = selectPlatformRole(
              platformRoles,
              user.id,
              user.defaultPlatformId,
              get().currentPlatform?.id ?? get().currentUser?.defaultPlatformId
            );
            const effectiveDefaultPlatformId = platformRole?.platformId ?? user.defaultPlatformId;
            const platformSettings = platformRole
              ? await UserRepository.getPlatformSettings(effectiveDefaultPlatformId)
              : undefined;

            const currentUser = new UserModel({
              ...user,
              defaultPlatformId: effectiveDefaultPlatformId,
              platformSettings,
              platformRoles,
              platformInvites,
            });
            set(() => ({ currentUser }));
          } catch (error) {
            log({
              name: 'useSessionStore.refetchCurrentUser',
              context: { error },
            });
          }
        },

        refetchPlatformConfig: async () => {
          const platformId = get().currentPlatform?.id;
          const isSandbox = get().isSandbox;
          if (!platformId) {
            return;
          }

          try {
            const platformConfig = await PlatformRepository.getConfig(platformId);
            const currentPlatform = get().currentPlatform;

            if (!currentPlatform || currentPlatform.id !== platformId || get().isSandbox !== isSandbox) {
              return;
            }

            set(() => ({
              currentPlatform: new PlatformModel({ ...currentPlatform, ...platformConfig }),
            }));
          } catch (error) {
            log({
              name: 'useSessionStore.refetchPlatformConfig',
              context: { error, platformId, isSandbox },
            });
          }
        },

        refetchPlatformSettings: async () => {
          const currentUser = get().currentUser;
          const platformId = currentUser?.defaultPlatformId;
          const platformRole = get().platformRole;

          if (!platformId || !currentUser || !platformRole) {
            return;
          }

          try {
            const { UserRepository } = await import('~/repositories');
            const platformSettings = await UserRepository.getPlatformSettings(platformId);

            set(() => ({ currentUser: new UserModel({ ...currentUser, platformSettings }) }));
          } catch (error) {
            log({
              name: 'useSessionStore.refetchPlatformSettings',
              context: { error },
            });
          }
        },

        loadSession: async (options) => {
          const userId = get().userId;
          const showGlobalLoading = options?.showGlobalLoading ?? true;

          if (!userId) {
            throw new Error('Please try to login again');
          }

          if (showGlobalLoading) {
            get().setLoading(true);
          }

          try {
            const { UserRepository } = await import('~/repositories');
            const user = await UserRepository.fetchSelf();
            const userSettings = await UserRepository.getSettings();
            useConfigStore.getState().loadTableSettings(userSettings.tableSettings);

            if (!user?.id) {
              throw new Error('Unauthorized');
            }

            const [{ platformRoles }, { invites: platformInvites }] = await Promise.all([
              UserRepository.platformRoles({ dashboardUserId: userId }),
              listDashboardUserInvites(userId),
            ]);

            const platformRole = selectPlatformRole(
              platformRoles,
              userId,
              user.defaultPlatformId,
              options?.preferredPlatformId ?? get().currentPlatform?.id ?? get().currentUser?.defaultPlatformId
            );
            const effectiveDefaultPlatformId = platformRole?.platformId ?? user.defaultPlatformId;

            const platformSettings = platformRole
              ? await UserRepository.getPlatformSettings(effectiveDefaultPlatformId)
              : undefined;

            const currentUser = new UserModel({
              ...user,
              defaultPlatformId: effectiveDefaultPlatformId,
              platformSettings,
              platformRoles,
              platformInvites,
            });

            set(() => ({
              currentUser,
              platformRole,
              currentPermission: platformRole?.platformLevelPermissions,
              loggedIn: true,
            }));

            if (platformRole) {
              const platform = await get().loadPlatform({ user: currentUser });

              if (typeof platform === 'undefined') {
                return {};
              }
            }

            get().setSettings(userSettings);

            get().triggerEvent('signin');
            get().triggerEvent('change');

            if (showGlobalLoading) {
              setTimeout(() => get().setLoading(false), 0);
            }

            startMonitoringSession(user, get().currentPlatform);

            return { currentPlatform: get().currentPlatform, currentUser: get().currentUser };
          } catch (error) {
            get().signOut();

            if (error === 'Unauthorized') {
              return {};
            }

            if (error instanceof PlatformError) {
              throw error;
            }

            log({
              name: 'useSessionStore.loadSession',
              context: { error, userId, currentUser: get().currentUser, currentPlatform: get().currentPlatform },
            });

            throw new Error('User error, contact support@column.com');
          } finally {
            if (showGlobalLoading) {
              get().setLoading(false);
            }
          }
        },

        signOut: () => {
          log({
            name: 'Signing out user',
            context: get().currentUser,
          });

          get().clearCookies();
          useConfigStore.getState().resetTableSettings();

          set(() => ({
            currentUser: undefined,
            currentPlatform: undefined,
            userId: undefined,
            loggedIn: false,
            loginAuthMethod: undefined,
            mfaStatus: undefined,
          }));

          get().setLoading(false);

          stopMonitoringSession();
          get().triggerEvent('signout');
          get().triggerEvent('change');
        },

        triggerEvent: (event: SessionStoreEvent) => {
          if (get().eventListeners.has(event)) {
            for (const listener of get().eventListeners.get(event)!) {
              listener();
            }
          }
        },

        addEventListener: (event: SessionStoreEvent, listener: SessionStoreListener) => {
          if (!get().eventListeners.has(event)) {
            get().eventListeners.set(event, []);
          }
          get().eventListeners.get(event)?.push(listener);
        },

        setCurrentUser: (user: UserModel) => {
          set(() => ({
            currentUser: user,
          }));
        },
      }),
      {
        name: 'column-dashboard',
        version: 1.2,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          userId: state.userId,
          loggedIn: state.loggedIn,
          isSandbox: state.isSandbox,
          loginAuthMethod: state.loginAuthMethod,
          mfaStatus: state.mfaStatus,
          params: state.params,
          settings: state.settings,
        }),
      }
    )
  )
);
